import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import HomepageNav from '../components/HomepageNav';
import MobileNav from '../components/MobileNav';
import Footer from '../components/Footer';
import { fetchNewsOverview, fetchNewsArchive, fetchArticleDetails } from '../services/xmlService';
import './News.css';

const News = () => {
  const location = useLocation();
  const [newsArticles, setNewsArticles] = useState([]);
  const [archiveArticles, setArchiveArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(false);

  useEffect(() => {
    loadNews();
  }, []);

  // Lade Artikel aus URL Parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const articleId = params.get('article');
    if (articleId && !selectedArticle) {
      handleArticleClick(articleId);
    }
  }, [location.search]);

  const loadNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const news = await fetchNewsOverview();
      setNewsArticles(news);
    } catch (err) {
      setError('News konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadArchive = async () => {
    if (archiveArticles.length > 0) {
      setShowArchive(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const archive = await fetchNewsArchive();
      setArchiveArticles(archive);
      setShowArchive(true);
    } catch (err) {
      setError('Archiv konnte nicht geladen werden. Bitte versuchen Sie es später erneut.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleArticleClick = async (articleId) => {
    setLoadingArticle(true);
    setError(null);
    try {
      const article = await fetchArticleDetails(articleId);
      setSelectedArticle(article);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Artikeldetails konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      console.error(err);
    } finally {
      setLoadingArticle(false);
    }
  };

  const handleBackToList = () => {
    setSelectedArticle(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Format: "11/2025" -> "November 2025"
    const parts = dateString.split('/');
    if (parts.length === 2) {
      const month = parseInt(parts[0]);
      const year = parts[1];
      const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
                          'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
      if (month >= 1 && month <= 12) {
        return `${monthNames[month - 1]} ${year}`;
      }
    }
    return dateString;
  };

  const decodeHtmlEntities = (text) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const renderArticleList = (articles, title) => (
    <div className="news-page__list">
      <h2 className="news-page__list-title">{title}</h2>
      {articles.length === 0 ? (
        <p className="news-page__empty">Keine Artikel verfügbar.</p>
      ) : (
        <div className="news-page__grid">
          {articles.map((article) => (
            <article
              key={article.id}
              className="news-page__card"
              onClick={() => handleArticleClick(article.id)}
            >
              {article.image && (
                <div className="news-page__card-image-wrapper">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="news-page__card-image"
                  />
                </div>
              )}
              <div className="news-page__card-content">
                <div className="news-page__card-header">
                  {article.date && (
                    <span className="news-page__card-date">{formatDate(article.date)}</span>
                  )}
                </div>
                <h3 className="news-page__card-title">{article.title}</h3>
                {article.teaser && (
                  <p className="news-page__card-teaser">{article.teaser}</p>
                )}
                <button className="news-page__card-link">
                  Weiterlesen
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );

  const renderArticleDetail = () => (
    <div className="news-page__detail">
      <button className="news-page__back-btn" onClick={handleBackToList}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Zurück zur Übersicht
      </button>

      <article className="news-page__article">
        <header className="news-page__article-header">
          {selectedArticle.category && (
            <span className="news-page__article-category">{selectedArticle.category}</span>
          )}
          <h1 className="news-page__article-title">{selectedArticle.title}</h1>
          {selectedArticle.date && (
            <time className="news-page__article-date">{formatDate(selectedArticle.date)}</time>
          )}
        </header>

        {selectedArticle.images && selectedArticle.images.length > 0 && (
          <div className="news-page__article-images">
            {selectedArticle.images.map((image, index) => (
              <figure key={index} className="news-page__article-image">
                <img src={image.url} alt={image.caption || selectedArticle.title} />
                {image.caption && <figcaption>{image.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}

        {selectedArticle.teaser && (
          <div className="news-page__article-teaser">
            <p>{decodeHtmlEntities(selectedArticle.teaser)}</p>
          </div>
        )}

        {selectedArticle.content && (
          <div 
            className="news-page__article-content"
            dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(selectedArticle.content) }}
          />
        )}

        {selectedArticle.signets && selectedArticle.signets.length > 0 && (
          <div className="news-page__article-signets">
            {selectedArticle.signets.map((signet, index) => (
              <img 
                key={index} 
                src={signet.url} 
                alt={signet.alt} 
                className="news-page__article-signet"
              />
            ))}
          </div>
        )}
      </article>
    </div>
  );

  return (
    <div className="news-page">
      <HomepageNav />
      <MobileNav />
      
      <main className="news-page__content">
        <div className="news-page__header">
          <h1 className="news-page__title">Steuer News</h1>
          <p className="news-page__subtitle">Bleiben Sie informiert über die neuesten steuerlichen Entwicklungen und Änderungen</p>
        </div>

        {error && (
          <div className="news-page__error">
            <p>{error}</p>
            <button onClick={loadNews} className="news-page__retry-btn">
              Erneut versuchen
            </button>
          </div>
        )}

        {loading && !selectedArticle && (
          <div className="news-page__loading">
            <div className="news-page__spinner"></div>
            <p>News werden geladen...</p>
          </div>
        )}

        {loadingArticle && (
          <div className="news-page__loading">
            <div className="news-page__spinner"></div>
            <p>Artikel wird geladen...</p>
          </div>
        )}

        {!loading && !loadingArticle && !error && (
          <>
            {selectedArticle ? (
              renderArticleDetail()
            ) : (
              <>
                {renderArticleList(newsArticles, 'Aktuelle News')}
                
                <div className="news-page__archive-section">
                  {!showArchive ? (
                    <button 
                      className="news-page__archive-btn"
                      onClick={loadArchive}
                    >
                      <img src="/img/archive.svg" alt="" aria-hidden="true" />
                      Archiv anzeigen
                    </button>
                  ) : (
                    <>
                      {renderArticleList(archiveArticles, 'Archiv')}
                      <button 
                        className="news-page__archive-btn news-page__archive-btn--hide"
                        onClick={() => setShowArchive(false)}
                      >
                        Archiv ausblenden
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default News;

